import createAppSlice from '../../app/createAppSlice';
import {
    FlyoutContent,
    IFlyoutState,
} from './interface';
import {
    TOBI_BOT_ICON,
    TOBI_BOT_NAME,
    TOBI_BOT_PATH,
    TOBI_BOT_SUBLINE,
    TOBI_BOT_TOPIC,
    TOBI_WEBCHAT_CONTAINER_ID,
} from './constant';
import {
    loadTobiCSS,
    loadTobiScript,
} from './helper/loadTOBiResources';
import { RootState } from '../../app/store';

const initialState: IFlyoutState = {
    isTobiInitialized: false,
    contactInfo: null,
};

const appSlice = createAppSlice({
    name: 'Flyout',
    initialState,
    reducers: create => {
        const createAThunk = create.asyncThunk.withTypes<{
            rejectValue: string;
        }>();

        return {
            getContactInfoForPage: createAThunk<FlyoutContent, void>(() => {
                const { config } = window.globalPageOptions.flyout;
                const { pathname } = window.location;

                const defaultContactInfo = {
                    phoneNumber: window.globalPageOptions.flyout.phoneNumbers.standard,
                    openingHours: window.globalPageOptions.flyout.openingHours,
                };

                const { phoneNumber, openingHours } = config
                    ?.find(candidate => candidate.matches
                        .some(urlPattern => {
                            if (urlPattern === '/') {
                                // This is the homepage
                                return pathname === urlPattern;
                            }

                            return pathname.includes(urlPattern);
                        }),
                    ) || defaultContactInfo;

                return {
                    phoneNumber,
                    openingHours,
                };
            }, {
                fulfilled: (state, action) => {
                    state.contactInfo = action.payload;
                },
            }),
            startTobi: createAThunk(async (_, { rejectWithValue, getState }) => {
                try {
                    if (!appSlice.selectors.selectIsTobiInitialized(getState() as RootState)) {
                        await Promise.all([
                            loadTobiCSS(),
                            loadTobiScript(),
                        ]);
                    }

                    const tobiChatContainer = document.getElementById(TOBI_WEBCHAT_CONTAINER_ID);

                    tobiChatContainer && window.webchatApi({
                        path: TOBI_BOT_PATH,
                        topic: TOBI_BOT_TOPIC,
                        botName: TOBI_BOT_NAME,
                        headline: TOBI_BOT_NAME,
                        subline: TOBI_BOT_SUBLINE,
                        imgIconSrc: TOBI_BOT_ICON,
                    }, tobiChatContainer);
                }
                catch (err) {
                    throw rejectWithValue((err as Error).message);
                }

                return;
            },
            {
                fulfilled: state => {
                    state.isTobiInitialized = true;
                },
            }),
            stopTobi: createAThunk<void, void>(() => {
                // This should stop the webchat itself.
                //  Unfortunately the webchat bundle (SAILS app) doesn't expose the disconnect logic to the window.
                //  We should maybe request that, because otherwise the tobi API keeps getting called every single second,
                //  even when the flyout is closed.
            }),
        };
    },
    selectors: {
        selectContactInfo: sliceState => sliceState.contactInfo,
        selectIsTobiInitialized: sliceState => sliceState.isTobiInitialized,
    },
});

export default appSlice;

export const {
    getContactInfoForPage,
    startTobi,
    stopTobi,
} = appSlice.actions;

export const {
    selectContactInfo,
    selectIsTobiInitialized,
} = appSlice.selectors;
